import { useTranslation } from 'react-i18next'

import LinkText from '@/components/LinkText/LinkText'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import Tag from '@/components/Tag/Tag'
import {
  useColumns,
  type RouteApiWithColumnsAndPagination
} from '@/hooks/useColumns'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { formatDate } from '@/utils/format-date'
import { getColumnWidth } from '@/utils/get-column-width'
import { timeWithoutSeconds } from '@/utils/time-without-seconds'

import { getAbsencesStatusTag } from '../constants/get-absences-status-tags'
import type { ClassAttendance } from '../queries/useClassAbsencesList'
import type { ClassAbsencePresence } from '../types'

type UseAbsencesColumnsProps = {
  presence: ClassAbsencePresence
  routeApi: RouteApiWithColumnsAndPagination
}

const useAbsencesColumns = ({
  presence,
  routeApi
}: UseAbsencesColumnsProps) => {
  const { t } = useTranslation('classes')
  const { isExtraLarge } = useScreenResolution()

  return useColumns<ClassAttendance>(
    [
      {
        key: 'student',
        label: t('header.student'),
        render: (value: ClassAttendance) => (
          <LinkText
            to="/students-and-classes/students/$studentId/details"
            params={{
              studentId: value.student.id
            }}
          >
            {value.student.name}
          </LinkText>
        )
      },
      {
        key: 'course',
        label: t('header.course'),
        dataIndex: ['lesson', 'course', 'name']
      },
      {
        key: 'time-range',
        label: t('header.time-range'),
        render: (_, { lesson }) =>
          `${timeWithoutSeconds(lesson.startTime)} - ${timeWithoutSeconds(lesson.endTime)}`,
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'date',
        label: t('header.date'),
        render: (_, record) => formatDate(record.lesson.date),
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'excusedBy',
        label: t('header.excused-by'),
        hidden: presence === 'unexcused',
        render: (value: ClassAttendance) =>
          value.absence.excusedBy ? (
            <LinkText
              {...(value.absence.excusedBy.isParent
                ? {
                    to: '/students-and-classes/students/parents/$parentId/details',
                    params: { parentId: value.absence.excusedBy.id }
                  }
                : {
                    to: '/users/$userId/details',
                    params: { userId: value.absence.excusedBy.id }
                  })}
            >
              {value.absence.excusedBy.name}
            </LinkText>
          ) : null
      },
      {
        key: 'status',
        label: t('header.status'),
        render: (_, record) => (
          <Tag {...getAbsencesStatusTag(record.absence.status)} />
        ),
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG)
      },
      {
        key: 'excusedOn',
        label: t('header.excused-on'),
        render: (_, record) =>
          record.absence.excusedAt
            ? formatDate(new Date(record.absence.excusedAt))
            : undefined,
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD),
        hidden: presence === 'unexcused'
      }
    ],
    { routeApi }
  )
}

export default useAbsencesColumns
